import { combineReducers } from 'redux';
import queryString from 'query-string';
import { takeLatest, takeEvery, call, select, put } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import PromiseCreator from 'promise-creator';
import dedent from 'dedent';
import { SubmissionError } from 'redux-form';
import get from 'lodash.get';
import request from '../api-utils/request';
import actionKey from '../utils/action-key';
import { showSnackbarAction } from './snackbar';
import { showAlert } from './alert';
import { openPromptAction, showPromptErrorAction, closePromptAction } from './prompt';

export const SEARCH_PROGRAMS = 'manage-programs/SEARCH_PROGRAMS';
export const CHANGE_FILTER = 'manage-programs/CHANGE_FILTER';
export const REMOVE_PROGRAM = 'manage-programs/REMOVE_PROGRAM';
export const FETCH_COMPANIES = 'manage-programs/FETCH_COMPANIES';
export const ADD_PROGRAM = 'manage-programs/ADD_PROGRAM';
export const UPDATE_PROGRAM = 'manage-programs/UPDATE_PROGRAM';
export const REMOVE_ALL_PROGRAMS = 'manage-programs/REMOVE_ALL_PROGRAMS';
export const FETCH_PROGRAM = 'manage-programs/FETCH_PROGRAM';
export const BULK_UPLOAD = 'manage-programs/BULK_UPLOAD';

export const programsList = (state = {}, action) => {
    switch (action.type) {
        case actionKey.success(SEARCH_PROGRAMS):
            return {
                ...state,
                ...action.payload.programs.reduce((acc, program) => {
                    acc[program.collegeProgramID_PK] = { // eslint-disable-line no-param-reassign
                        ...program,
                    };
                    return acc;
                }, {}),
            };
        case actionKey.success(UPDATE_PROGRAM):
            return {
                ...state,
                [action.payload.program.collegeProgramID_PK]: action.payload.program,
            };
        case actionKey.success(FETCH_PROGRAM):
            return {
                ...state,
                [action.payload.program.collegeProgramID_PK]: action.payload.program,
            };
        default:
            return state;
    }
};

export const maxReached = (state = false, action) => {
    switch (action.type) {
        case SEARCH_PROGRAMS:
            return false;
        case actionKey.success(SEARCH_PROGRAMS):
            return action.payload.programs.length < 10;
        default:
            return state;
    }
};

export const searchList = (state = [], action) => {
    switch (action.type) {
        case actionKey.init(SEARCH_PROGRAMS): {
            if (action.offset !== 0) {
                return state;
            }
            return [];
        }
        case actionKey.success(SEARCH_PROGRAMS):
            return [
                ...state,
                ...action.payload.programs,
            ];
        case actionKey.success(REMOVE_PROGRAM):
            return state.filter(id => id.collegeProgramID_PK !== action.payload);
        case actionKey.success(REMOVE_ALL_PROGRAMS):
            return state.filter(id => id.companyID_FK !== action.payload.companyID_FK);
        default:
            return state;
    }
};

export const loading = (state = null, action) => {
    switch (action.type) {
        case CHANGE_FILTER:
        case SEARCH_PROGRAMS:
            return true;
        case actionKey.success(SEARCH_PROGRAMS):
        case actionKey.failure(SEARCH_PROGRAMS):
            return false;
        default:
            return state;
    }
};

export const errorReducer = (state = null, action) => {
    switch (action.type) {
        case SEARCH_PROGRAMS:
        case actionKey.success(SEARCH_PROGRAMS):
            return null;
        case actionKey.failure(SEARCH_PROGRAMS):
            return action.error;
        default:
            return state;
    }
};

export const filter = (state = {}, action) => {
    switch (action.type) {
        case actionKey.success(FETCH_COMPANIES): {
            const company = action.payload.companies.find(({ havePrograms }) => havePrograms);
            if (!company) {
                return state;
            }
            return {
                ...state,
                companyId: company.companyID_PK,
            };
        }
        case SEARCH_PROGRAMS:
            return {
                ...state,
                ...action.payload,
            };
        case CHANGE_FILTER: {
            const newState = {
                ...state,
                ...action.payload,
            };
            newState.offset = action.payload.offset || 0;
            return newState;
        }
        case actionKey.init(SEARCH_PROGRAMS): {
            if (typeof action.q === 'undefined') {
                return {
                    ...state,
                    q: '',
                };
            }
            return state;
        }
        default:
            return state;
    }
};

export const empty = (state = false, action) => {
    switch (action.type) {
        case SEARCH_PROGRAMS:
            return false;
        case actionKey.success(SEARCH_PROGRAMS):
            return !action.q &&
                (!action.payload.programs || action.payload.programs.length === 0) &&
                (!action.offset || action.offset === 0);
        default:
            return state;
    }
};

export const initialLoading = (state = true, action) => {
    switch (action.type) {
        case actionKey.init(SEARCH_PROGRAMS):
            return typeof action.q === 'undefined';
        case actionKey.success(SEARCH_PROGRAMS):
        case actionKey.failure(SEARCH_PROGRAMS):
            return false;
        default:
            return state;
    }
};

export const companies = (state = [], action) => {
    switch (action.type) {
        case actionKey.success(FETCH_COMPANIES):
            return action.payload.companies;
        case actionKey.success(ADD_PROGRAM): {
            const companyId = Number(action.payload.program.companyID_FK);
            return state.map(company => ({
                ...company,
                havePrograms: (
                    company.havePrograms ||
                    company.companyID_PK === companyId
                ),
            }));
        }
        case actionKey.success(UPDATE_PROGRAM): {
            const companyId = Number(action.payload.program.companyID_FK);
            return state.map(company => ({
                ...company,
                havePrograms: (
                    company.havePrograms ||
                    company.companyID_PK === companyId
                ),
            }));
        }
        default:
            return state;
    }
};

export const companiesLoading = (state = false, action) => {
    switch (action.type) {
        case FETCH_COMPANIES:
            return true;
        case actionKey.success(FETCH_COMPANIES):
        case actionKey.failure(FETCH_COMPANIES):
            return false;
        default:
            return state;
    }
};

export default combineReducers({
    error: errorReducer,
    loading,
    list: programsList,
    search: searchList,
    filter,
    initialLoading,
    empty,
    maxReached,
    companies,
    companiesLoading,
});

/**
 * REDUX ACTIONS
 */

export const searchManageProgramsAction = (payload = {}) => ({
    type: SEARCH_PROGRAMS,
    payload,
});

export const changeFilterAction = payload => ({
    type: CHANGE_FILTER,
    payload,
});

export const removeProgramAction = payload => ({
    type: REMOVE_PROGRAM,
    payload,
});

export const fetchCompaniesAction = () => ({
    type: FETCH_COMPANIES,
});

export const fetchProgramAction = payload => ({
    type: FETCH_COMPANIES,
    payload,
});

export const addProgramAction = (payload, { resolve, reject } = {}) => ({
    type: ADD_PROGRAM,
    payload,
    resolve,
    reject,
});

export const removeAllProgramsAction = (payload, { resolve, reject } = {}) => ({
    type: REMOVE_ALL_PROGRAMS,
    payload,
    resolve,
    reject,
});

export const updateProgramAction = (payload, { resolve, reject } = {}) => ({
    type: UPDATE_PROGRAM,
    payload,
    resolve,
    reject,
});

export const bulkUploadAction = (payload, { resolve, reject } = {}) => ({
    type: BULK_UPLOAD,
    payload,
    resolve,
    reject,
});

/**
 * SAGA LISTENERS
 */

export function* rootSaga() {
    yield [
        takeLatest(SEARCH_PROGRAMS, searchManagePrograms),
        takeLatest(CHANGE_FILTER, searchOnChange),
        takeLatest([
            actionKey.success(ADD_PROGRAM),
            actionKey.success(BULK_UPLOAD),
            actionKey.success(FETCH_COMPANIES),
        ], refreshManageProgramsList),
        takeEvery([
            actionKey.failure(REMOVE_PROGRAM),
            actionKey.failure(REMOVE_ALL_PROGRAMS),
            actionKey.failure(FETCH_PROGRAM),
        ], handleFailure),
        takeEvery(REMOVE_PROGRAM, removeProgram),
        takeEvery(FETCH_COMPANIES, fetchCompanies),
        takeEvery(UPDATE_PROGRAM, updateProgram),
        takeEvery(REMOVE_ALL_PROGRAMS, removeAllPrograms),
        takeEvery(ADD_PROGRAM, addProgram),
        takeEvery(FETCH_PROGRAM, fetchProgram),
        takeEvery(BULK_UPLOAD, bulkUpload),
    ];
}

export function* refreshManageProgramsList() {
    yield put(changeFilterAction({ offset: 0 }));
}

export function* searchOnChange(action) {
    if (action.payload.q) {
        yield call(delay, 350);
    }
    yield put(searchManageProgramsAction());
}

export function* searchManagePrograms() {
    const { managePrograms } = yield select();
    const params = queryString.stringify(managePrograms.filter);
    yield call(request, {
        url: `/api/administration/college-programs?${params}`,
        type: SEARCH_PROGRAMS,
        ...managePrograms.filter,
    });
}

export function* fetchProgram({ type, payload }) {
    yield call(request, {
        url: `/api/administration/college-programs/${payload}`,
        type,
    });
}
const noop = () => null;

export function* updateProgram({ type, payload, resolve = noop, reject = noop }) {
    const result = yield call(request, {
        url: `/api/administration/college-programs/${payload.collegeProgramID_PK}`,
        options: {
            method: 'PUT',
            body: payload,
        },
        type,
    });

    if (result.error) {
        reject(new SubmissionError({
            _error: result.error,
        }));
        return;
    }
    resolve();
}

export function* addProgram({ type, payload, resolve = noop, reject = noop }) {
    const result = yield call(request, {
        url: '/api/administration/college-programs',
        options: {
            method: 'POST',
            body: payload,
        },
        type,
    });
    if (result.error) {
        reject(new SubmissionError({
            _error: result.error,
        }));
        return;
    }
    resolve();
}

export function* bulkUpload({ type, payload, resolve = noop, reject = noop }) {
    const result = yield call(request, {
        url: '/api/administration/college-programs/bulk',
        options: {
            method: 'POST',
            body: payload,
        },
        type,
    });
    if (result.error) {
        reject(new SubmissionError({
            _error: result.error,
        }));
        return;
    }
    resolve();
    const { managePrograms } = yield select();
    const {
        companyID_FK, // eslint-disable-line camelcase
        programs,
    } = payload;
    const company = managePrograms.companies.find(c => (
        c.companyID_PK === Number(companyID_FK) // eslint-disable-line camelcase
    ));
    yield put(showAlert({
        title: 'Program Upload Successuful',
        message: dedent`
            ${programs.length} programs were successfully uploaded${company ? ` to the ${company.companyName} domain` : ''}.
        `,
        type: 'prompt',
        button: 'Close',
    }));
}

export function* removeProgram({ type, payload }) {
    const { promise, resolve } = PromiseCreator();
    const { managePrograms } = yield select();
    const program = managePrograms.list[payload];
    yield put(openPromptAction({
        resolve,
        title: 'Remove Program',
        message: dedent`
            Are you sure you want to remove the ${get(program, 'name')} program?
        `,
        okButton: 'Remove',
        primary: 'ok',
        showLoading: true,
    }));
    const remove = yield promise;
    if (remove) {
        const result = yield call(request, {
            url: `/api/administration/college-programs/${payload}`,
            options: {
                method: 'DELETE',
            },
            type,
            payload,
        });
        if (result.error) {
            yield put(showPromptErrorAction(result.error));
            yield removeProgram({ type, payload });
            return;
        }
    }
    yield put(closePromptAction());
}

export function* removeAllPrograms({ type, payload, resolve = noop, reject = noop }) {
    const result = yield call(request, {
        url: '/api/administration/college-programs/delete-all',
        options: {
            method: 'POST',
            body: payload,
        },
        type,
    });
    if (result.error) {
        reject(new SubmissionError({
            _error: result.error,
        }));
        return;
    }
    resolve();
    yield put(closePromptAction());
}

export function* fetchCompanies({ type }) {
    yield call(request, {
        url: '/api/administration/college-programs/companies',
        type,
    });
}

export function* handleFailure({ error }) {
    yield put(showSnackbarAction({
        message: error,
        type: 'failure',
        timeout: 5000,
    }));
}



// WEBPACK FOOTER //
// ./src/client/redux/manage-programs.js